import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type Project = {
  id: number;
  name: string;
  description: string | null;
  theme_color: string;
  archived: number;
  my_role: string;
};

type Template = {
  id: number;
  name: string;
  description: string | null;
  theme_color: string | null;
};

export default function ProjectList() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#81B29A');
  const [templateId, setTemplateId] = useState<string>('');
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function refresh() {
    const r = await api.get<{ projects: Project[] }>(
      `/projects${showArchived ? '?archived=1' : ''}`
    );
    setProjects(r.projects);
  }

  useEffect(() => {
    refresh();
    api.get<{ templates: Template[] }>('/templates').then((r) => setTemplates(r.templates));
  }, [showArchived]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post<{ id: number }>('/projects', {
        name,
        description: description || null,
        theme_color: color,
        template_id: templateId ? Number(templateId) : undefined,
      });
      setName('');
      setDescription('');
      setTemplateId('');
      setCreating(false);
      // Notifie la sidebar (et autres consommateurs) de rafraîchir
      window.dispatchEvent(new CustomEvent('projects:changed'));
      nav(`/projets/${r.id}/documents`);
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive(p: Project) {
    await api.patch(`/projects/${p.id}/archive`, { archived: p.archived === 0 });
    window.dispatchEvent(new CustomEvent('projects:changed'));
    refresh();
  }

  const active = projects.filter((p) => p.archived === 0);
  const archived = projects.filter((p) => p.archived === 1);

  return (
    <>
      <div className="topbar">
        <h1>Projets</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn btn-ghost ${showArchived ? 'btn-pill-active' : ''}`}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Masquer archivés' : 'Voir archivés'}
          </button>
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Annuler' : '+ Nouveau projet'}
          </button>
        </div>
      </div>

      {creating && (
        <form className="card" onSubmit={onCreate} style={{ marginBottom: 20 }}>
          <div className="field">
            <label>Nom du projet</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label>Description (facultatif)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
            <div className="field">
              <label>Couleur</label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: 80, padding: 2, height: 40 }}
              />
            </div>
            <div className="field">
              <label>Gabarit</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">— Projet vide (défauts)</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.description ? ` — ${t.description}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Création…' : 'Créer'}
          </button>
        </form>
      )}

      {active.length === 0 && archived.length === 0 ? (
        <div className="card card-meta">Aucun projet. Crée ton premier projet ci-dessus.</div>
      ) : (
        <>
          <div className="grid-2">
            {active.map((p) => (
              <div key={p.id} className="card" style={{ position: 'relative' }}>
                <Link to={`/projets/${p.id}/documents`} style={{ display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: p.theme_color,
                      }}
                    />
                    <div className="card-title">{p.name}</div>
                  </div>
                  {p.description && <div className="card-meta">{p.description}</div>}
                  <div className="card-meta" style={{ marginTop: 8 }}>
                    Ton rôle : <strong>{p.my_role}</strong>
                  </div>
                </Link>
                <button
                  className="btn btn-ghost"
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    padding: '4px 10px',
                    fontSize: 12,
                  }}
                  onClick={() => toggleArchive(p)}
                >
                  Archiver
                </button>
              </div>
            ))}
          </div>

          {showArchived && archived.length > 0 && (
            <>
              <div className="section-title">
                <h3>Archivés</h3>
                <span className="card-meta">{archived.length}</span>
              </div>
              <div className="grid-2">
                {archived.map((p) => (
                  <div key={p.id} className="card" style={{ opacity: 0.7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: p.theme_color,
                        }}
                      />
                      <div className="card-title">{p.name}</div>
                    </div>
                    <button
                      className="btn btn-ghost"
                      style={{ marginTop: 8, fontSize: 12, padding: '4px 10px' }}
                      onClick={() => toggleArchive(p)}
                    >
                      Désarchiver
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
